import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import './DocumentUpload.css';

// Simple Icons
const Icon = ({ name, size = 20 }) => {
    const icons = {
        upload: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
        file: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><polyline points="13 2 13 9 20 9" /></svg>,
        trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
        check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
    };
    return icons[name] || icons.file;
};

const DocumentUpload = ({ entityType, entityId, onUploadComplete, existingDocuments = [] }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [documents, setDocuments] = useState(existingDocuments);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = [...e.dataTransfer.files];
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    const handleFileInput = (e) => {
        const files = [...e.target.files];
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    const handleFiles = async (files) => {
        // Process each file
        const newDocs = files.map(file => ({
            name: file.name,
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            type: file.type,
            status: 'pending', // pending, uploading, success, error
            file: file
        }));

        setDocuments([...documents, ...newDocs]);

        // Simulate upload for now (You would replace this with actual API call)
        await uploadFiles(newDocs);
    };

    const uploadFiles = async (filesToUpload) => {
        setUploading(true);

        // In a real implementation, you would use FormData and call your API
        /*
        const formData = new FormData();
        filesToUpload.forEach(f => formData.append('files', f.file));
        formData.append('entityType', entityType);
        if (entityId) formData.append('entityId', entityId);
        
        try {
          const response = await api.uploadDocuments(formData);
          // handled success
        } catch (err) {
          // handle error
        }
        */

        // Simulating upload delay
        for (let i = 0; i < filesToUpload.length; i++) {
            await new Promise(resolve => setTimeout(resolve, 800));
            // Update status of specific file (simplified for this demo)
        }

        // Mark all as success for this demo
        const updatedDocs = documents.map(d => ({ ...d, status: 'success' }));
        const brandNewDocs = filesToUpload.map(d => ({ ...d, status: 'success' }));

        const finalDocs = [...documents.filter(d => !filesToUpload.includes(d)), ...brandNewDocs];

        setDocuments(finalDocs);
        setUploading(false);

        if (onUploadComplete) {
            onUploadComplete(finalDocs);
        }

        toast.success(`${filesToUpload.length} document(s) uploaded successfully`);
    };

    const removeDocument = (index) => {
        const newDocs = [...documents];
        newDocs.splice(index, 1);
        setDocuments(newDocs);
        if (onUploadComplete) {
            onUploadComplete(newDocs);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    return (
        <div className="document-upload-container">
            <div
                className={`upload-dropzone ${isDragging ? 'active' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={triggerFileInput}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInput}
                    multiple
                    className="hidden-file-input"
                    style={{ display: 'none' }}
                />
                <div className="upload-icon">
                    <Icon name="upload" size={32} />
                </div>
                <div className="upload-text">
                    <span className="primary-text">Click to upload</span> or drag and drop
                </div>
                <div className="upload-hint">
                    Any file type up to 10MB
                </div>
            </div>

            {documents.length > 0 && (
                <div className="documents-list">
                    <h4>Attached Documents ({documents.length})</h4>
                    {documents.map((doc, index) => (
                        <div key={index} className="document-item">
                            <div className="doc-icon">
                                <Icon name="file" size={20} />
                            </div>
                            <div className="doc-info">
                                <span className="doc-name">{doc.name}</span>
                                <span className="doc-size">{doc.size}</span>
                            </div>
                            {doc.status === 'success' && (
                                <div className="doc-status success">
                                    <Icon name="check" size={16} />
                                </div>
                            )}
                            <button
                                type="button"
                                className="btn-remove-doc"
                                onClick={(e) => { e.stopPropagation(); removeDocument(index); }}
                            >
                                <Icon name="trash" size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DocumentUpload;
